import { useState, useEffect, useRef } from 'react';
import TextInput from './TextInput';
import { fetchAddressSuggestions, parseAddressFeature } from '../../utils/updateUserFonctions';
import { isValidAddress, isValidCity, isValidZipCode } from '../../utils/Validator';
import clsx from 'clsx';
const AddressInputGroup = ({ value, onChange, onValidationChange }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isAddressSelected, setIsAddressSelected] = useState(false);
  const debounceTimer = useRef(null);
  const suggestionsRef = useRef(null);

  // Check if address is complete (all 3 fields filled and valid)
  const isAddressEmpty = !value.rue && !value.ville && !value.codePostal;
  const isAddressComplete = 
    value.rue && 
    value.ville && 
    value.codePostal &&
    isValidAddress(value.rue) &&
    isValidCity(value.ville) &&
    isValidZipCode(value.codePostal);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Notify parent when address validation changes
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isAddressEmpty || isAddressComplete);
    }
  }, [isAddressEmpty, isAddressComplete, onValidationChange]);

  const handleAddressChange = (val) => {
    onChange('rue', val);
    
    // Clear ville and codePostal when user starts typing again
    if (isAddressSelected && val !== value.rue) {
      onChange('ville', '');
      onChange('codePostal', '');
      setIsAddressSelected(false);
    }

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(async () => {
      if (val && val.length >= 3) {
        setIsLoading(true);
        const results = await fetchAddressSuggestions(val);
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
        setIsLoading(false);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 200);
  };

  const handleSuggestionClick = (feature) => {
    const address = parseAddressFeature(feature);
    
    onChange('rue', address.rue);
    onChange('ville', address.ville);
    onChange('codePostal', address.codePostal);
    
    setIsAddressSelected(true);
    
    setShowSuggestions(false);
    setSuggestions([]);
  };


  return (
    <div className="relative">
      <div className="relative mb-4">
        <TextInput
          type="text"
          maxLength={40}
          placeholder="Rechercher une adresse..."
          value={value.rue}
          onChange={handleAddressChange}
          isValid={isAddressEmpty || isAddressComplete}
        />
        
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-600 pointer-events-none">
            ...
          </div>
        )}

        {showSuggestions && suggestions.length > 0 && (
          <div
            ref={suggestionsRef}
            className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg mt-1 max-h-[200px] overflow-y-auto shadow-lg z-50"
          >
            {suggestions.map((feature, index) => (
              <div
                key={index}
                onClick={() => handleSuggestionClick(feature)}
                className={clsx(
                  'p-3 cursor-pointer transition-colors hover:bg-gray-50',
                  {
                    'border-b border-gray-100': index < suggestions.length - 1,
                  },
                )}
              >
                <div className="text-2xl font-medium mb-1">
                  {feature.properties.name}
                </div>
                <div className="text-xl text-gray-600">
                  {feature.properties.postcode} {feature.properties.city}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      
      <div className="flex flex-col gap-4">
      <TextInput
        type="text"
        maxLength={20}
        placeholder="Ville"
        value={value.ville}
        isValid={isAddressEmpty || isAddressComplete}
        disabled={true}
      />

      <TextInput
        type="text"
        maxLength={10}
        placeholder="Code postal"
        value={value.codePostal}
        isValid={isAddressEmpty || isAddressComplete}
        disabled={true}
      />
      </div>
    </div>
  );
};

export default AddressInputGroup;
